/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { When, If, Then, Else } from "react-if";
import { useParams } from "react-router";
import {
  Box,
  Col,
  Row,
  theme,
  FlexContainer,
  Message,
  Text,
} from "@nordcloud/gnui";
import {
  CloudProviderType,
  EstateV2Filter,
  SearchSortField,
  SearchSortOrder,
  useResourcesNewLazyQuery,
  useMetricResourceLazyQuery,
  MetricResourceQuery,
  ResourcesNewQuery,
} from "~/generated/graphql";
import {
  ListLoader,
  LoaderWrap,
  Pagination,
  ReactTable,
  PaginationState,
  ErrorWrap,
} from "~/components";
import {
  KUBERNETES_METRICS_COLUMNS,
  PROVIDER_ICON_COLUMN,
} from "~/components/Tables/Columns";
import { ERROR_COLORS } from "~/constants";
import {
  estateV2FilterFields,
  useCurrency,
  useEstateSort,
  useEstateFilters,
  useFilteredQueryState,
  useScrollAfterPaging,
} from "~/hooks";
import { isNotEmpty } from "~/tools";
import { HYBRID_PROVIDERS } from "~/views/accounts/AddAccount/constants";
import {
  EstateSort,
  EstateSearchBox,
  EstateActiveFilters,
  EstateFiltersToggle,
  EstateData,
} from "~/views/estate/components";
import { convertTags, generateRowData } from "~/views/estate/helpers";
import { ResourcesList } from "../../components/ResourcesList";
import { Estate } from "../../components/types";
import { ACCOUNT_DETAILS_ESTATE_COLUMNS } from "./columns";
import { mapEstateRecord } from "./mapping";

type RouteParams = {
  accountId: string;
};

type SortingProp = {
  field?: SearchSortField;
  order?: SearchSortOrder;
};

type EstateQueryState = EstateV2Filter &
  PaginationState &
  SortingProp & {
    tags: string[];
    metadata: string[];
    query: string;
  };

type Props = {
  providerId: string;
  providerType: string;
};

type CommonListProps = {
  hasResources: boolean;
  data: ResourcesNewQuery | undefined;
  columns: unknown;
  rows: Estate[];
  showPagination: boolean;
  estateCount: number;
};

type KubernetesListProps = {
  hasMetricsResources: boolean;
  metricsEnabled: boolean;
  metrics: MetricResourceQuery | undefined;
  metricColumns: unknown;
  metricDetails: unknown;
  showMetricsPagination: boolean;
  metricsCount: number;
  metadata: CommonListProps;
};

export function EstateRecords({ providerId, providerType }: Props) {
  const scrollToRef = React.useRef<HTMLDivElement>(null);

  const metricsEnabled = enableMetrics(providerType);

  const { currency } = useCurrency();

  const { accountId } = useParams<RouteParams>();

  const { state, updateQueryState } =
    useFilteredQueryState(estateV2FilterFields);

  // @ts-expect-error ignored temporary after migration to TypeScript 4.9
  const {
    page,
    limit,
    query,
    field,
    order,
    tags,
    metadata,
    ...rawFilters
  }: EstateQueryState = state;
  const [searchInput, setSearchInput] = React.useState(query);

  const { sortField, sortDirection, onSortSelect, onSortOrderChange } =
    // @ts-expect-error ignored temporary after migration to TypeScript 4.9
    useEstateSort({ field, order, updateQueryState });

  const filters = {
    ...rawFilters,
    tags: convertTags(tags),
    metadata: convertTags(metadata),
  };

  const getAccount = () =>
    providerType === CloudProviderType.Kubernetes ? accountId : providerId;

  const [getEstateRecords, { data, loading, error }] = useResourcesNewLazyQuery(
    {
      variables: {
        page,
        limit,
        sort: {
          field: sortField,
          order: sortDirection,
        },
        filter: {
          ...filters,
          account: [getAccount()],
        },
        query,
      },
    }
  );

  const [
    getMetricsRecords,
    { data: metrics, loading: loadingMetrics, error: loadingError },
  ] = useMetricResourceLazyQuery({
    variables: {
      limit,
      page,
      sort: {
        field: sortField,
        order: sortDirection,
      },
      filter: {
        ...filters,
        account: [providerId],
      },
      query,
    },
  });

  React.useEffect(() => {
    if (isNotEmpty(providerId)) {
      getEstateRecords();
    }

    if (metricsEnabled) {
      getMetricsRecords();
    }
  }, [providerId, providerType, metricsEnabled]);

  const rows = React.useMemo(
    () =>
      data?.estateV2?.resources?.map((record) =>
        mapEstateRecord(record, currency)
      ) ?? [],
    [data, currency]
  );

  const metricDetails = React.useMemo(() => {
    const metricResults = metrics?.estateV2?.resources ?? [];

    return metricResults.map((res) => ({
      ...generateRowData(res, currency),
    }));
  }, [metrics, currency]);

  const columns = React.useMemo(() => ACCOUNT_DETAILS_ESTATE_COLUMNS, []);
  const metricColumns = React.useMemo(
    () => [PROVIDER_ICON_COLUMN, ...KUBERNETES_METRICS_COLUMNS],
    []
  );

  const hasResources = isNotEmpty(rows);
  const hasMetricsResources = isNotEmpty(metricDetails);

  const estateCount = data?.estateV2?.count ?? 0;
  const metricsCount = metrics?.estateV2?.count ?? 0;
  const showPagination = estateCount > 0;
  const showMetricsPagination = metricsCount > 0;

  const { activeFilters, removeFields, clearFilters, removeFilterValue } =
    useEstateFilters(filters);
  const showFilters = isNotEmpty(activeFilters);

  useScrollAfterPaging({ scrollToRef, enabled: !loading || !loadingMetrics });

  const commonListProps: CommonListProps = {
    hasResources,
    data,
    columns,
    rows,
    showPagination,
    estateCount,
  };

  return (
    <div ref={scrollToRef}>
      <ErrorWrap
        error={error || loadingError}
        status={
          hasResources || hasMetricsResources
            ? ERROR_COLORS.notification
            : ERROR_COLORS.danger
        }
      >
        <Message
          status="notification"
          image="info"
          width="100%"
          css={{
            marginBottom: theme.spacing.spacing04,
          }}
        >
          <FlexContainer direction="column" alignItems="flex-start">
            <Text
              weight="medium"
              color={theme.color.text.info}
              mb={theme.spacing.spacing02}
            >
              The list includes only Cloud Estate Records.
            </Text>
            <Text mb={theme.spacing.spacing00} color={theme.color.text.text02}>
              The difference between the selected account’s monthly cost and the
              sum of estate records costs is that we don’t include “networking,”
              “discount,” “support,” and “other” costs. Instead, those costs are
              grouped into the additional cost resources.
            </Text>
          </FlexContainer>
        </Message>
        <Box boxStyle="lightGrey" spacing="spacing04">
          <FlexContainer justifyContent="space-between" alignItems="center">
            <EstateSort
              sortField={sortField}
              sortDirection={sortDirection}
              onSortSelect={onSortSelect}
              onSortOrderChange={onSortOrderChange}
            />
            <div
              css={{
                margin: `0 ${theme.spacing.spacing05}`,
                flexGrow: 1,
              }}
            >
              <EstateSearchBox
                searchInput={searchInput}
                setSearchInput={setSearchInput}
              />
            </div>
            <EstateFiltersToggle
              isAccount
              allowSavingFilters={false}
              onApply={() => setSearchInput(query ?? "")}
            />
          </FlexContainer>
          <When condition={showFilters}>
            <Row>
              <Col md="12">
                <div
                  css={{
                    marginTop: theme.spacing.spacing04,
                    marginLeft: theme.spacing.spacing02,
                  }}
                >
                  <EstateActiveFilters
                    filters={activeFilters}
                    allowSavingFilters={false}
                    onRemoveKey={removeFields}
                    onClear={clearFilters}
                    onRemoveValue={(itemName, index) =>
                      removeFilterValue(itemName as keyof EstateV2Filter, index)
                    }
                  />
                </div>
              </Col>
            </Row>
          </When>
        </Box>
        <LoaderWrap
          isBackground
          loading={loading || loadingMetrics}
          Component={ListLoader}
        >
          <If condition={metricsEnabled}>
            <Then>
              <ResourcesListKubernetes
                hasMetricsResources={hasMetricsResources}
                metrics={metrics}
                metricsEnabled={metricsEnabled}
                metricColumns={metricColumns}
                metricDetails={metricDetails}
                showMetricsPagination={showMetricsPagination}
                metricsCount={metricsCount}
                metadata={commonListProps}
              />
            </Then>
            <Else>
              <ResourcesList metadata={commonListProps} />
            </Else>
          </If>
        </LoaderWrap>
      </ErrorWrap>
    </div>
  );
}

function ResourcesListKubernetes({
  hasMetricsResources,
  metricsEnabled,
  metrics,
  metricColumns,
  metricDetails,
  showMetricsPagination,
  metricsCount,
  metadata,
}: KubernetesListProps) {
  return (
    <If condition={hasMetricsResources && metricsEnabled}>
      <Then>
        <div css={{ marginBottom: theme.spacing.spacing04 }}>
          <EstateData
            currentMonthTotal={metrics?.estateV2?.cost?.currentMonthTotal}
            count={metrics?.estateV2?.count}
          />
        </div>
        <ReactTable
          striped
          columns={metricColumns}
          data={metricDetails}
          sort={false}
        />
        {showMetricsPagination && <Pagination count={metricsCount} />}
      </Then>
      <Else>
        <ResourcesList metadata={metadata} />
      </Else>
    </If>
  );
}

function enableMetrics(providerType: string) {
  return (
    providerType === HYBRID_PROVIDERS.openShift ||
    providerType === HYBRID_PROVIDERS.kubernetes
  );
}
